import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import clinicalNoteService from './clinicalNoteService.js';
import './ClinicalNoteGoalCompact.css';

/**
 * Delay between the clinician's final edit and autosave.
 */
const AUTOSAVE_DELAY_MS = 900;

/**
 * Resolve ClinicalNoteMain.entityVersion from a note response.
 */
function resolveEntityVersion(response) {
  if (!response) {
    return null;
  }

  return (
    response.entityVersion ??
    response.note?.entityVersion ??
    response.clinicalNote?.entityVersion ??
    null
  );
}

/**
 * A goal is uniquely represented in the note by its goal template.
 */
function getGoalKey(row) {
  return String(
    row?.goalTemplateId ??
      row?.clinicalNoteGoalId ??
      'unknown'
  );
}

/**
 * Convert nullable backend text into a controlled input value.
 */
/**
 * Only enabled STG/LTG targets count toward required goal completeness.
 * A displayed template default is useful, but a missing default must not be
 * marked Saved or silently treated as an accepted clinical target.
 */
function hasCompleteGoalTargets(row) {
  return Boolean(row?.shortTermEnabled || row?.longTermEnabled) &&
    (!row.shortTermEnabled || String(row.draftShortTermTargetValue ?? '').trim() !== '') &&
    (!row.longTermEnabled || String(row.draftLongTermTargetValue ?? '').trim() !== '');
}

function missingGoalTargets(row) {
  const missing = [];
  if (row?.shortTermEnabled && !String(row.draftShortTermTargetValue ?? '').trim()) {
    missing.push('Short-Term target');
  }
  if (row?.longTermEnabled && !String(row.draftLongTermTargetValue ?? '').trim()) {
    missing.push('Long-Term target');
  }
  return missing;
}

function toInputValue(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return '';
  }

  return String(value);
}

/**
 * Normalize one GoalResponse into frontend draft fields.
 */
function normalizeGoalRow(row) {
  return {
    ...row,

    draftShortTermTargetValue:
      toInputValue(
        row?.shortTermTargetValue
      ),

    draftShortTermCurrentValue:
      toInputValue(
        row?.shortTermCurrentValue
      ),

    draftShortTermStatus:
      row?.shortTermStatus ?? '',

    draftLongTermTargetValue:
      toInputValue(
        row?.longTermTargetValue
      ),

    draftLongTermCurrentValue:
      toInputValue(
        row?.longTermCurrentValue
      ),

    draftLongTermStatus:
      row?.longTermStatus ?? '',

    draftPatientSpecificGoalText:
      toInputValue(
        row?.patientSpecificGoalText
      ),
  };
}

/**
 * Sort goals according to backend display order.
 */
function sortGoalRows(rows) {
  return [...rows].sort(
    (first, second) => {
      const firstOrder =
        Number.isFinite(
          Number(
            first?.displayOrder
          )
        )
          ? Number(
              first.displayOrder
            )
          : 0;

      const secondOrder =
        Number.isFinite(
          Number(
            second?.displayOrder
          )
        )
          ? Number(
              second.displayOrder
            )
          : 0;

      if (
        firstOrder !==
        secondOrder
      ) {
        return (
          firstOrder -
          secondOrder
        );
      }

      return String(
        first?.displayName ??
          first?.goalCode ??
          ''
      ).localeCompare(
        String(
          second?.displayName ??
            second?.goalCode ??
            ''
        )
      );
    }
  );
}

/**
 * Determine whether one enum value exists in backend metadata.
 */
function hasStatusOption(
  statusOptions,
  value
) {
  if (!value) {
    return true;
  }

  return statusOptions.some(
    (option) =>
      option?.value === value
  );
}

/**
 * Resolve a clinician-facing GoalStatus label.
 */
function getStatusDisplay(
  statusOptions,
  value
) {
  if (!value) {
    return '—';
  }

  const option =
    statusOptions.find(
      (candidate) =>
        candidate?.value === value
    );

  return (
    option?.displayValue ??
    value
  );
}

/**
 * Validate only values that must be structurally valid before an
 * autosave request can be sent.
 *
 * Required clinical completeness remains backend review validation.
 */
function validateGoalRow(
  row,
  statusOptions
) {
  const errors = {};

  if (
    row.shortTermEnabled &&
    row.draftShortTermStatus &&
    !hasStatusOption(
      statusOptions,
      row.draftShortTermStatus
    )
  ) {
    errors.shortTermStatus =
      'The selected Short-Term Goal status is not recognized by the server.';
  }

  if (
    row.longTermEnabled &&
    row.draftLongTermStatus &&
    !hasStatusOption(
      statusOptions,
      row.draftLongTermStatus
    )
  ) {
    errors.longTermStatus =
      'The selected Long-Term Goal status is not recognized by the server.';
  }

  return errors;
}

/**
 * Produce the goal wording the clinician currently sees.
 *
 * Patient-specific wording overrides the configured snapshot only when
 * that template explicitly allows customization.
 */
function getEffectiveGoalText(row) {
  if (
    row?.allowPatientSpecificEdit
  ) {
    const custom =
      row
        ?.draftPatientSpecificGoalText
        ?.trim();

    if (custom) {
      return custom;
    }
  }

  return (
    row?.goalTextSnapshot ??
    ''
  );
}

/**
 * Format the template's age restriction for informational display.
 *
 * Eligibility itself is enforced by Spring Boot.
 */
function formatAgeRestriction(
  minAge,
  maxAge
) {
  const minimum =
    minAge === null ||
    minAge === undefined
      ? null
      : Number(minAge);

  const maximum =
    maxAge === null ||
    maxAge === undefined
      ? null
      : Number(maxAge);

  if (
    minimum === null &&
    maximum === null
  ) {
    return '';
  }

  if (
    minimum !== null &&
    maximum !== null
  ) {
    return (
      `Ages ${minimum}–${maximum}`
    );
  }

  if (minimum !== null) {
    return (
      `Age ${minimum}+`
    );
  }

  return (
    `Age ${maximum} or younger`
  );
}

/**
 * Merge a successful GoalResponse with current local draft state.
 *
 * If another user edit occurred while the save request was in flight,
 * preserve that newer draft while still refreshing backend warning and
 * previous-visit metadata.
 */
function mergeSavedGoalRow(
  currentRow,
  savedResponse,
  preserveDraft
) {
  const normalized =
    normalizeGoalRow(
      savedResponse
    );

  if (!preserveDraft) {
    return normalized;
  }

  return {
    ...normalized,

    draftShortTermTargetValue:
      currentRow
        .draftShortTermTargetValue,

    draftShortTermCurrentValue:
      currentRow
        .draftShortTermCurrentValue,

    draftShortTermStatus:
      currentRow
        .draftShortTermStatus,

    draftLongTermTargetValue:
      currentRow
        .draftLongTermTargetValue,

    draftLongTermCurrentValue:
      currentRow
        .draftLongTermCurrentValue,

    draftLongTermStatus:
      currentRow
        .draftLongTermStatus,

    draftPatientSpecificGoalText:
      currentRow
        .draftPatientSpecificGoalText,
  };
}

/**
 * Reusable field used for STG/LTG target/current values.
 */
function GoalValueField({
  id,
  label,
  value,
  unit,
  disabled,
  onChange,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          marginBottom: 5,
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {label}
      </label>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
        }}
      >
        <input
          id={id}
          type="text"
          value={value}
          disabled={disabled}
          onChange={(event) =>
            onChange(
              event.target.value
            )
          }
          style={{
            width: '100%',
            minWidth: 0,
            boxSizing:
              'border-box',
            minHeight: 38,
            border:
              '1px solid #bbb',
            borderRadius: 6,
            padding:
              '7px 9px',
            fontSize: 14,
            background:
              disabled
                ? '#f3f4f6'
                : '#fff',
          }}
        />

        {unit && (
          <span
            style={{
              flexShrink: 0,
              fontSize: 12,
              color: '#666',
            }}
          >
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Reusable GoalStatus selector.
 */
function GoalStatusField({
  id,
  label,
  value,
  statusOptions,
  disabled,
  error,
  onChange,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          marginBottom: 5,
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {label}
      </label>

      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(event) =>
          onChange(
            event.target.value
          )
        }
        aria-invalid={Boolean(
          error
        )}
        style={{
          width: '100%',
          boxSizing:
            'border-box',
          minHeight: 38,
          border:
            error
              ? '1px solid #b42318'
              : '1px solid #bbb',
          borderRadius: 6,
          padding:
            '7px 9px',
          fontSize: 14,
          background:
            disabled
              ? '#f3f4f6'
              : '#fff',
        }}
      >
        <option value="">
          Select status
        </option>

        {statusOptions.map(
          (option) => (
            <option
              key={
                option.value
              }
              value={
                option.value
              }
            >
              {option.displayValue}
            </option>
          )
        )}
      </select>

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 5,
            fontSize: 12,
            color: '#b42318',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * Previous-visit summary for one Short-Term or Long-Term Goal.
 */
function PreviousGoalSummary({
  title,
  currentValue,
  status,
  statusOptions,
  unit,
}) {
  const hasPrevious =
    Boolean(
      currentValue ||
      status
    );

  return (
    <div
      style={{
        border:
          '1px solid #e1e4e8',
        borderRadius: 7,
        background:
          '#fafafa',
        padding: 9,
      }}
    >
      <div
        style={{
          marginBottom: 4,
          fontSize: 11,
          fontWeight: 700,
          textTransform:
            'uppercase',
          color: '#777',
        }}
      >
        {title}
      </div>

      {!hasPrevious && (
        <div
          style={{
            fontSize: 12,
            color: '#777',
          }}
        >
          No previous goal progress
        </div>
      )}

      {hasPrevious && (
        <div
          style={{
            display: 'grid',
            gap: 3,
            fontSize: 12,
          }}
        >
          <div>
            <strong>
              Current:
            </strong>{' '}

            {currentValue ||
              '—'}

            {currentValue &&
              unit && (
                <>
                  {' '}
                  {unit}
                </>
              )}
          </div>

          <div>
            <strong>
              Status:
            </strong>{' '}

            {getStatusDisplay(
              statusOptions,
              status
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * One complete goal editor card.
 */
function GoalRow({
  row,
  statusOptions,
  initialEvaluation = false,
  disabled,
  saveStatus,
  saveError,
  validationErrors,
  backendIssues = [],
  onFieldChange,
  onRetry,
}) {
  const key =
    getGoalKey(row);
  // Collapsing only hides the editor; it must remain mounted for autosave.
  const [expanded, setExpanded] = useState(false);

  const [
    showShortProgress,
    setShowShortProgress,
  ] = useState(
    Boolean(
      row?.draftShortTermCurrentValue ||
      row?.draftShortTermStatus
    )
  );

  const [
    showLongProgress,
    setShowLongProgress,
  ] = useState(
    Boolean(
      row?.draftLongTermCurrentValue ||
      row?.draftLongTermStatus
    )
  );

  useEffect(() => {
    if (
      row?.draftShortTermCurrentValue ||
      row?.draftShortTermStatus
    ) {
      setShowShortProgress(true);
    }
  }, [
    row?.draftShortTermCurrentValue,
    row?.draftShortTermStatus,
  ]);

  useEffect(() => {
    if (
      row?.draftLongTermCurrentValue ||
      row?.draftLongTermStatus
    ) {
      setShowLongProgress(true);
    }
  }, [
    row?.draftLongTermCurrentValue,
    row?.draftLongTermStatus,
  ]);

  const effectiveText =
    getEffectiveGoalText(
      row
    );

  const ageRestriction =
    formatAgeRestriction(
      row.minAge,
      row.maxAge
    );

  const repetitionWarning =
    Boolean(
      row.repetitionWarning
    );

  const unresolvedWarning =
    repetitionWarning &&
    !row.warningAcknowledged;

  // The backend is authoritative. Only this goal's issues affect its card.
  const blockingIssues = backendIssues.filter((issue) => issue.blocking);
  const advisoryIssues = backendIssues.filter((issue) => issue.warning && !issue.blocking);
  const missingTargetLabels = missingGoalTargets(row);
  const hasBlockingGoalIssue = blockingIssues.length > 0 || missingTargetLabels.length > 0;
  const hasGoalWarning = advisoryIssues.length > 0 || unresolvedWarning;
  useEffect(() => {
    if (saveStatus === 'error' || saveStatus === 'invalid') {
      setExpanded(true);
    }
  }, [saveStatus]);
  const isGoalSaving = saveStatus === 'saving' || saveStatus === 'unsaved';
  const cardTone = saveStatus === 'error' || hasBlockingGoalIssue
    ? 'error'
    : hasGoalWarning || isGoalSaving || saveStatus === 'incomplete'
      ? 'warning'
      : 'complete';

  // Initial Evaluation and Progress Notes use template goals by default.
  // This is a display label only; backend validation remains authoritative.
  const showDefaultGoalBadge =
    initialEvaluation && Boolean(row.required);

  // The top-right header reports the same per-goal state as the card.
  // Network save states take precedence so clinicians never mistake an
  // unsaved or failed edit for an accepted clinical goal.
  let headerLabel;
  let headerColor;

  if (saveStatus === 'error') {
    headerLabel = 'Autosave failed';
    headerColor = '#b42318';
  } else if (saveStatus === 'invalid') {
    headerLabel = 'Needs correction';
    headerColor = '#b42318';
  } else if (saveStatus === 'saving') {
    headerLabel = 'Saving…';
    headerColor = '#555';
  } else if (saveStatus === 'unsaved') {
    headerLabel = 'Unsaved';
    headerColor = '#9a6700';
  } else if (saveStatus === 'incomplete' || cardTone === 'error') {
    headerLabel = 'Needs attention';
    headerColor = '#b42318';
  } else if (cardTone === 'warning') {
    headerLabel = 'Review';
    headerColor = '#9a6700';
  } else {
    headerLabel = 'Complete';
    headerColor = '#2e6a35';
  }

  return (
    <article
      style={{
        border: cardTone === 'error'
          ? '1px solid #c94b45'
          : cardTone === 'warning'
            ? '1px solid #d6a100'
            : '1px solid #9bc9a3',

        borderRadius: 8,

        background: cardTone === 'error'
          ? '#fffafa'
          : cardTone === 'warning'
            ? '#fffdf5'
            : '#fff',

        overflow: 'hidden',
      }}
    >
      {/* ===================================================
          GOAL HEADER
          =================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems:
            'flex-start',
          gap: 12,
          flexWrap: 'wrap',
          padding: 13,
          borderBottom:
            '1px solid #e5e5e5',
          background:
            '#fafafa',
        }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              gap: 7,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <strong
              style={{
                fontSize: 15,
              }}
            >
              {row.displayName ||
                row.goalCode ||
                'Clinical Goal'}
            </strong>

            {row.required && (
              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '2px 7px',
                  background: showDefaultGoalBadge
                    ? '#fff3cd'
                    : '#fce8e6',
                  color: showDefaultGoalBadge
                    ? '#875f00'
                    : '#8a1c13',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {showDefaultGoalBadge ? 'Default' : 'Required'}
              </span>
            )}

            {ageRestriction && (
              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '2px 7px',
                  background:
                    '#eef3f8',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {ageRestriction}
              </span>
            )}
          </div>

          {row.goalCode && (
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: '#666',
              }}
            >
              {row.goalCode}
            </div>
          )}
        </div>

        <div
          aria-live="polite"
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: headerColor,
          }}
        >
          <span>{headerLabel}</span>
          <button
            type="button"
            className="clinical-goal-compact-toggle"
            onClick={() => setExpanded((current) => !current)}
            aria-expanded={expanded}
            aria-controls={`clinical-goal-detail-${key}`}
          >
            {expanded ? 'Minimize' : 'Edit / Expand'}
          </button>
        </div>
      </div>

      {!expanded && (
        <div className="clinical-goal-compact-summary">
          <span className="clinical-goal-compact-summary__text">{effectiveText || 'Clinical Goal'}</span>
          {row.shortTermEnabled && (
            <span className="clinical-goal-compact-summary__target">STG: {row.draftShortTermTargetValue || 'Target missing'}{row.draftShortTermTargetValue && row.valueUnit ? ` ${row.valueUnit}` : ''}</span>
          )}
          {row.longTermEnabled && (
            <span className="clinical-goal-compact-summary__target">LTG: {row.draftLongTermTargetValue || 'Target missing'}{row.draftLongTermTargetValue && row.valueUnit ? ` ${row.valueUnit}` : ''}</span>
          )}
          {missingTargetLabels.length > 0 && <span className="clinical-goal-compact-summary__missing">Missing {missingTargetLabels.join(', ')}</span>}
          {saveError && <span className="clinical-goal-compact-summary__missing">Autosave failed — expand to retry</span>}
        </div>
      )}
      <div
        id={`clinical-goal-detail-${key}`}
        hidden={!expanded}
        className="clinical-goal-compact-body"
        style={{
          padding: 12,
          display: expanded ? 'block' : 'none',
        }}
      >
        {/* Per-goal validation, separate from the overall Goals status. */}
        {(cardTone !== 'complete') && <div aria-live="polite" style={{
          marginBottom: 8,
          padding: '9px 11px',
          borderRadius: 7,
          background: cardTone === 'error' ? '#fff1f0' : cardTone === 'warning' ? '#fff8e1' : '#edf8ef',
          color: cardTone === 'error' ? '#a52a24' : cardTone === 'warning' ? '#806000' : '#286436',
          fontSize: 12,
          fontWeight: 700,
        }}>
          {cardTone === 'error' ? 'Needs attention' : 'Review / default values'}
        </div>}

        {missingTargetLabels.length > 0 && (
          <div role="alert" style={{ color: '#a52a24', fontSize: 12, marginBottom: 9 }}>
            Missing required target: {missingTargetLabels.join(', ')}.
          </div>
        )}

        {backendIssues.length > 0 && (
          <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
            {backendIssues.map((issue, index) => (
              <div key={`${issue.issueCode}-${issue.fieldCode || ''}-${index}`}
                role={issue.blocking ? 'alert' : 'status'}
                style={{
                  borderLeft: `3px solid ${issue.blocking ? '#c94b45' : '#d6a100'}`,
                  padding: '5px 9px',
                  fontSize: 12,
                  color: issue.blocking ? '#a52a24' : '#755900',
                  background: issue.blocking ? '#fff3f2' : '#fffaf0',
                }}>
                {issue.message}
              </div>
            ))}
          </div>
        )}

        {/* =================================================
            GOAL WORDING
            ================================================= */}
        <div
          style={{
            marginBottom: 14,
          }}
        >
          <div
            style={{
              marginBottom: 5,
              fontSize: 12,
              fontWeight: 700,
              textTransform:
                'uppercase',
              color: '#666',
            }}
          >
            Goal
          </div>

          <div
            style={{
              border:
                '1px solid #d9e2ec',
              borderRadius: 7,
              background:
                '#f7fafc',
              padding: 10,
              fontSize: 14,
              lineHeight: 1.5,
              whiteSpace:
                'pre-wrap',
            }}
          >
            {effectiveText ||
              'No goal wording configured.'}
          </div>
        </div>

        {/* =================================================
            PATIENT-SPECIFIC WORDING
            ================================================= */}
        {row.allowPatientSpecificEdit && (
          <details className="clinical-goal-compact-details">
            <summary>Patient-specific goal wording{row.draftPatientSpecificGoalText ? ' (entered)' : ' (optional)'}</summary>
          <div style={{ marginTop: 10 }}>
            <label
              htmlFor={`goal-custom-${key}`}
              style={{
                display: 'block',
                marginBottom: 5,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              Patient-Specific Goal
              Wording
            </label>

            <textarea
              id={`goal-custom-${key}`}
              rows={3}
              disabled={disabled}
              value={
                row
                  .draftPatientSpecificGoalText
              }
              onChange={(event) =>
                onFieldChange(
                  'draftPatientSpecificGoalText',
                  event.target.value
                )
              }
              placeholder={
                row.goalTextSnapshot
                  ? 'Leave blank to use the standard goal wording above.'
                  : 'Enter patient-specific goal wording.'
              }
              style={{
                width: '100%',
                boxSizing:
                  'border-box',
                resize: 'vertical',
                border:
                  '1px solid #bbb',
                borderRadius: 6,
                padding: 9,
                fontSize: 13,
                lineHeight: 1.5,
                background:
                  disabled
                    ? '#f3f4f6'
                    : '#fff',
              }}
            />

            <div
              style={{
                marginTop: 5,
                fontSize: 11,
                color: '#666',
              }}
            >
              Leave this blank to retain
              the configured standard
              wording.
            </div>
          </div>
          </details>
        )}

        {/* =================================================
            SHORT-TERM GOAL
            ================================================= */}
        {row.shortTermEnabled && (
          <section
            style={{
              border:
                '1px solid #e0e0e0',
              borderRadius: 8,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <h4
              style={{
                margin:
                  '0 0 11px',
                fontSize: 14,
              }}
            >
              Short-Term Goal
            </h4>

            {initialEvaluation && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  marginBottom: 10,
                  fontSize: 12,
                  color: '#555',
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    showShortProgress
                  }
                  disabled={disabled}
                  onChange={(event) =>
                    setShowShortProgress(
                      event.target.checked
                    )
                  }
                />
                Document optional current/progress information
              </label>
            )}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit, minmax(170px, 1fr))',
                gap: 11,
              }}
            >
              <GoalValueField
                id={`goal-st-target-${key}`}
                label="Target"
                value={
                  row
                    .draftShortTermTargetValue
                }
                unit={
                  row.valueUnit
                }
                disabled={
                  disabled
                }
                onChange={(value) =>
                  onFieldChange(
                    'draftShortTermTargetValue',
                    value
                  )
                }
              />

              {(!initialEvaluation ||
                showShortProgress) && (
                <>
                  <GoalValueField
                    id={`goal-st-current-${key}`}
                    label="Current / Progress"
                    value={
                      row
                        .draftShortTermCurrentValue
                    }
                    unit={
                      row.valueUnit
                    }
                    disabled={
                      disabled
                    }
                    onChange={(value) =>
                      onFieldChange(
                        'draftShortTermCurrentValue',
                        value
                      )
                    }
                  />

                  <GoalStatusField
                    id={`goal-st-status-${key}`}
                    label="Status"
                    value={
                      row
                        .draftShortTermStatus
                    }
                    statusOptions={
                      statusOptions
                    }
                    disabled={
                      disabled
                    }
                    error={
                      validationErrors
                        ?.shortTermStatus ||
                      ''
                    }
                    onChange={(value) =>
                      onFieldChange(
                        'draftShortTermStatus',
                        value
                      )
                    }
                  />
                </>
              )}
            </div>

            <details className="clinical-goal-compact-details">
              <summary>Previous Short-Term Visit</summary>
              <div style={{ marginTop: 9 }}>
              <PreviousGoalSummary
                title="Previous Short-Term Progress"
                currentValue={
                  row
                    .previousShortTermCurrentValue
                }
                status={
                  row
                    .previousShortTermStatus
                }
                statusOptions={
                  statusOptions
                }
                unit={
                  row.valueUnit
                }
              />
              </div>
            </details>
          </section>
        )}

        {/* =================================================
            LONG-TERM GOAL
            ================================================= */}
        {row.longTermEnabled && (
          <section
            style={{
              border:
                '1px solid #e0e0e0',
              borderRadius: 8,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <h4
              style={{
                margin:
                  '0 0 11px',
                fontSize: 14,
              }}
            >
              Long-Term Goal
            </h4>

            {initialEvaluation && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  marginBottom: 10,
                  fontSize: 12,
                  color: '#555',
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    showLongProgress
                  }
                  disabled={disabled}
                  onChange={(event) =>
                    setShowLongProgress(
                      event.target.checked
                    )
                  }
                />
                Document optional current/progress information
              </label>
            )}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit, minmax(170px, 1fr))',
                gap: 11,
              }}
            >
              <GoalValueField
                id={`goal-lt-target-${key}`}
                label="Target"
                value={
                  row
                    .draftLongTermTargetValue
                }
                unit={
                  row.valueUnit
                }
                disabled={
                  disabled
                }
                onChange={(value) =>
                  onFieldChange(
                    'draftLongTermTargetValue',
                    value
                  )
                }
              />

              {(!initialEvaluation ||
                showLongProgress) && (
                <>
                  <GoalValueField
                    id={`goal-lt-current-${key}`}
                    label="Current / Progress"
                    value={
                      row
                        .draftLongTermCurrentValue
                    }
                    unit={
                      row.valueUnit
                    }
                    disabled={
                      disabled
                    }
                    onChange={(value) =>
                      onFieldChange(
                        'draftLongTermCurrentValue',
                        value
                      )
                    }
                  />

                  <GoalStatusField
                    id={`goal-lt-status-${key}`}
                    label="Status"
                    value={
                      row
                        .draftLongTermStatus
                    }
                    statusOptions={
                      statusOptions
                    }
                    disabled={
                      disabled
                    }
                    error={
                      validationErrors
                        ?.longTermStatus ||
                      ''
                    }
                    onChange={(value) =>
                      onFieldChange(
                        'draftLongTermStatus',
                        value
                      )
                    }
                  />
                </>
              )}
            </div>

            <details className="clinical-goal-compact-details">
              <summary>Previous Long-Term Visit</summary>
              <div style={{ marginTop: 9 }}>
              <PreviousGoalSummary
                title="Previous Long-Term Progress"
                currentValue={
                  row
                    .previousLongTermCurrentValue
                }
                status={
                  row
                    .previousLongTermStatus
                }
                statusOptions={
                  statusOptions
                }
                unit={
                  row.valueUnit
                }
              />
              </div>
            </details>
          </section>
        )}

        {/* =================================================
            TEMPLATE WITH NO STG/LTG
            ================================================= */}
        {!row.shortTermEnabled &&
          !row.longTermEnabled && (
            <div
              style={{
                border:
                  '1px dashed #bbb',
                borderRadius: 7,
                background:
                  '#fafafa',
                padding: 10,
                fontSize: 12,
                color: '#666',
                marginBottom: 12,
              }}
            >
              This goal template does not
              currently enable a
              Short-Term or Long-Term
              progress section.
            </div>
          )}

        {initialEvaluation && missingTargetLabels.length > 0 && (
          <div role="alert" style={{
            marginTop: 10, padding: 10, border: '1px solid #efb7b1',
            background: '#fff5f5', borderRadius: 7, color: '#8a1c13',
            fontSize: 12,
          }}>
            Required {missingTargetLabels.join(' and ')} missing. Enter a
            clinically appropriate target to enable saving and review.
            Unconfigured values are never generated automatically.
          </div>
        )}

        {/* =================================================
            REPETITION WARNING
            ================================================= */}
        {repetitionWarning && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              border:
                '1px solid #d6a100',
              borderRadius: 8,
              background:
                '#fff8df',
              padding: 11,
            }}
          >
            <div
              style={{
                fontWeight: 700,
                marginBottom: 5,
              }}
            >
              Goal Progress Warning
            </div>

            <div
              style={{
                fontSize: 13,
              }}
            >
              The documented goal
              progress has remained
              unchanged across the
              configured number of
              consecutive clinical
              notes.
            </div>

            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: '#555',
              }}
            >
              {row.warningAcknowledged
                ? 'This warning has been acknowledged.'
                : 'This warning remains unresolved and must be corrected or acknowledged before the note completes review.'}
            </div>

            {row.warningAcknowledged &&
              row.warningReason && (
                <div
                  style={{
                    marginTop: 5,
                    fontSize: 12,
                    color: '#555',
                  }}
                >
                  Reason:{' '}
                  {row.warningReason}
                </div>
              )}

            {row.unchangedNoteLimit !==
              null &&
              row.unchangedNoteLimit !==
                undefined && (
                <div
                  style={{
                    marginTop: 5,
                    fontSize: 11,
                    color: '#777',
                  }}
                >
                  Configured unchanged
                  note limit:{' '}
                  {row.unchangedNoteLimit}
                </div>
              )}
          </div>
        )}

        {/* =================================================
            SAVE ERROR
            ================================================= */}
        {saveError && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              border:
                '1px solid #b42318',
              borderRadius: 8,
              background:
                '#fff5f5',
              padding: 11,
            }}
          >
            <strong>
              Goal was not saved.
            </strong>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
              }}
            >
              {saveError}
            </div>

            {!disabled && (
              <button
                type="button"
                onClick={onRetry}
                style={{
                  marginTop: 8,
                }}
              >
                Retry Save
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * Clinical Goals editor.
 *
 * Features:
 *
 * - backend-configured goal templates
 * - age-filtered goals from Spring Boot
 * - Short-Term and Long-Term Goal sections
 * - backend-driven GoalStatus options
 * - previous-visit comparison
 * - repetition-warning display
 * - optional patient-specific wording
 * - configured goal-text snapshot
 * - draft/incomplete values
 * - serialized autosave
 * - optimistic-lock version refresh
 */
export default function ClinicalNoteGoalSection({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator,
}) {
  const [
    rows,
    setRows,
  ] = useState([]);

  const [
    statusOptions,
    setStatusOptions,
  ] = useState([]);

  const [
    noteType,
    setNoteType,
  ] = useState('');

  // Both evaluation and progress documentation use template-default
  // targets with optional current/progress information in the editor.
  const initialEvaluation =
    noteType === 'INITIAL_EVALUATION' ||
    noteType === 'RE_EVALUATION' ||
    noteType === 'PROGRESS_NOTE';

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    loadError,
    setLoadError,
  ] = useState('');

  const [
    rowStatuses,
    setRowStatuses,
  ] = useState({});

  const [
    rowSaveErrors,
    setRowSaveErrors,
  ] = useState({});

  const [backendGoalIssues, setBackendGoalIssues] = useState([]);
  const validationRequestRef = useRef(0);
  const activeNoteIdRef = useRef(clinicalNoteId);
  activeNoteIdRef.current = clinicalNoteId;

  const [
    editTick,
    setEditTick,
  ] = useState(0);

  const [
    initialized,
    setInitialized,
  ] = useState(false);

  /*
   * Refs used by serialized autosaving.
   */
  const rowsRef =
    useRef([]);

  const statusOptionsRef =
    useRef([]);

  const versionRef =
    useRef(null);

  const rowRevisionRef =
    useRef({});

  const dirtyKeysRef =
    useRef(new Set());

  const failedKeysRef =
    useRef(new Set());

  const savingRef =
    useRef(false);

  const mountedRef =
    useRef(true);

  const flushSaveRef =
    useRef(null);

  // Every changed goal stays in this set until its exact revision saves.
  // The original dirty queue alone cannot account for invalid/failed rows.
  const unsavedKeysRef = useRef(new Set());
  const inFlightSaveRef = useRef(null);
  const initializedRef = useRef(false);
  const loadErrorRef = useRef('');

  useEffect(() => {
    rowsRef.current =
      rows;
  }, [rows]);

  useEffect(() => {
    statusOptionsRef.current =
      statusOptions;
  }, [statusOptions]);

  useEffect(() => {
    return () => {
      mountedRef.current =
        false;
    };
  }, []);

  /**
   * Load:
   *
   * - age-eligible configured/saved goals
   * - backend GoalStatus metadata
   * - current ClinicalNoteMain optimistic version
   */
  useEffect(() => {
    let cancelled = false;

    async function loadGoalSection() {
      setLoading(true);
      setLoadError('');
      loadErrorRef.current = '';
      initializedRef.current = false;
      setInitialized(false);

      setRowStatuses({});
      setRowSaveErrors({});
      setBackendGoalIssues([]);
      validationRequestRef.current += 1;

      dirtyKeysRef.current =
        new Set();
      unsavedKeysRef.current = new Set();

      failedKeysRef.current =
        new Set();

      rowRevisionRef.current =
        {};

      savingRef.current =
        false;

      try {
        const [
          goalResponse,
          goalStatusResponse,
          noteResponse,
          validationResponse,
        ] =
          await Promise.all([
            clinicalNoteService
              .getGoals(
                clinicalNoteId
              ),

            clinicalNoteService
              .getGoalStatuses(),

            clinicalNoteService
              .getClinicalNote(
                clinicalNoteId
              ),
            clinicalNoteService.validateClinicalNote(clinicalNoteId),
          ]);

        if (cancelled) {
          return;
        }

        const version =
          resolveEntityVersion(
            noteResponse
          );

        if (
          version === null ||
          version === undefined
        ) {
          throw new Error(
            'The clinical note version could not be loaded.'
          );
        }

        if (
          !Array.isArray(
            goalStatusResponse
          )
        ) {
          throw new Error(
            'Goal status metadata could not be loaded.'
          );
        }

        setBackendGoalIssues((validationResponse?.issues || []).filter(
          (issue) => issue.sectionCode === 'GOAL'
        ));

        versionRef.current =
          version;

        setNoteType(
          noteResponse?.noteType ??
          noteResponse?.note?.noteType ??
          noteResponse?.clinicalNote?.noteType ??
          ''
        );

        statusOptionsRef.current =
          goalStatusResponse;

        setStatusOptions(
          goalStatusResponse
        );

        const normalizedRows =
          sortGoalRows(
            (
              goalResponse ||
              []
            ).map(
              normalizeGoalRow
            )
          );

        const statuses =
          {};

        const revisions =
          {};

        /*
         * A visible goal target supplied by GoalTemplateMain is not yet
         * persisted. For any editable Draft, enqueue each new goal whose
         * enabled target fields have defaults. Missing defaults require a
         * clinician-entered target and remain visibly incomplete. Serialized
         * autosave queue creates permanent patient-specific snapshots.
         * Never perform this initialization on signed/locked notes.
         */
        const loadedStatus =
          noteResponse?.status ??
          noteResponse?.note?.status ??
          noteResponse?.clinicalNote?.status ??
          '';

        const canInitializeDefaults =
          loadedStatus === 'DRAFT' &&
          noteResponse?.locked !== true &&
          noteResponse?.note?.locked !== true &&
          noteResponse?.clinicalNote?.locked !== true &&
          !disabled;

        let queuedDefaults = false;

        normalizedRows.forEach(
          (row) => {
            const key =
              getGoalKey(row);

            const hasGoalTargets = hasCompleteGoalTargets(row);
            const hasMissingTargets = missingGoalTargets(row).length > 0;

            const shouldSaveDefault =
              canInitializeDefaults &&
              (row.clinicalNoteGoalId === null ||
                row.clinicalNoteGoalId === undefined) &&
              hasGoalTargets;

            statuses[key] = shouldSaveDefault
              ? 'unsaved'
              : canInitializeDefaults && hasMissingTargets
                ? 'incomplete'
                : 'saved';

            revisions[key] =
              shouldSaveDefault ? 1 : 0;

            if (shouldSaveDefault) {
              unsavedKeysRef.current.add(key);
              dirtyKeysRef.current.add(key);
              queuedDefaults = true;
            }
          }
        );

        rowsRef.current =
          normalizedRows;

        rowRevisionRef.current =
          revisions;

        setRows(
          normalizedRows
        );

        setRowStatuses(
          statuses
        );

        initializedRef.current = true;
        setInitialized(true);

        if (queuedDefaults) {
          setEditTick((current) => current + 1);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        loadErrorRef.current = error?.message || 'Clinical goals could not be loaded.';
        setLoadError(loadErrorRef.current);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (clinicalNoteId) {
      loadGoalSection();
    } else {
      setLoadError(
        'Clinical note ID is required.'
      );

      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [clinicalNoteId]);

  /**
   * Refresh goal-specific validation after a save. Ignore stale note switches.
   */
  const refreshGoalValidation = useCallback(async () => {
    const requestedNoteId = clinicalNoteId;
    const requestId = ++validationRequestRef.current;
    try {
      const validation = await clinicalNoteService.validateClinicalNote(requestedNoteId);
      if (mountedRef.current && activeNoteIdRef.current === requestedNoteId &&
          validationRequestRef.current === requestId) {
        setBackendGoalIssues((validation?.issues || []).filter(
          (issue) => issue.sectionCode === 'GOAL'
        ));
      }
    } catch (error) {
      // Validation refresh must not invalidate a successfully persisted goal.
      console.warn('Goal validation refresh failed:', error);
    }
  }, [clinicalNoteId]);

  /**
   * Refresh the parent note version after one goal autosave.
   */
  const refreshEntityVersion =
    useCallback(
      async () => {
        const noteResponse =
          await clinicalNoteService
            .getClinicalNote(
              clinicalNoteId
            );

        const newVersion =
          resolveEntityVersion(
            noteResponse
          );

        if (
          newVersion === null ||
          newVersion ===
            undefined
        ) {
          throw new Error(
            'The goal was saved, but the updated clinical note version could not be loaded.'
          );
        }

        versionRef.current =
          newVersion;

        if (
          typeof onVersionChange ===
          'function'
        ) {
          onVersionChange(
            newVersion,
            noteResponse
          );
        }

        return noteResponse;
      },
      [
        clinicalNoteId,
        onVersionChange,
      ]
    );

  /**
   * Change one local draft field and mark that goal dirty.
   */
  function updateRowField(
    key,
    field,
    value
  ) {
    if (disabled) {
      return;
    }

    const nextRows =
      rowsRef.current.map(
        (row) =>
          getGoalKey(row) ===
          key
            ? {
                ...row,
                [field]: value,
              }
            : row
      );

    rowsRef.current =
      nextRows;

    setRows(
      nextRows
    );

    rowRevisionRef.current = {
      ...rowRevisionRef.current,

      [key]:
        (
          rowRevisionRef
            .current[key] ??
          0
        ) + 1,
    };

    /*
     * Editing a previously failed row enables another autosave attempt.
     */
    failedKeysRef.current.delete(
      key
    );

    dirtyKeysRef.current.add(
      key
    );
    unsavedKeysRef.current.add(key);

    setRowSaveErrors(
      (current) => ({
        ...current,
        [key]: '',
      })
    );

    const editedRow =
      nextRows.find(
        (row) =>
          getGoalKey(row) ===
          key
      );

    const errors =
      editedRow
        ? validateGoalRow(
            editedRow,
            statusOptionsRef.current
          )
        : {};

    setRowStatuses(
      (current) => ({
        ...current,

        [key]:
          Object.keys(
            errors
          ).length > 0
            ? 'invalid'
            : 'unsaved',
      })
    );

    setEditTick(
      (current) =>
        current + 1
    );
  }

  /**
   * Merge the backend GoalResponse after a successful autosave.
   */
  function applySuccessfulSave(
    key,
    response,
    revisionBeingSaved
  ) {
    const currentRevision =
      rowRevisionRef.current[
        key
      ] ?? 0;

    const preserveDraft =
      currentRevision !==
      revisionBeingSaved;

    const nextRows =
      rowsRef.current.map(
        (row) => {
          if (
            getGoalKey(row) !==
            key
          ) {
            return row;
          }

          return mergeSavedGoalRow(
            row,
            response,
            preserveDraft
          );
        }
      );

    rowsRef.current =
      sortGoalRows(
        nextRows
      );

    setRows(
      rowsRef.current
    );

    if (!preserveDraft) unsavedKeysRef.current.delete(key);

    if (preserveDraft) {
      dirtyKeysRef.current.add(
        key
      );

      setRowStatuses(
        (current) => ({
          ...current,
          [key]: 'unsaved',
        })
      );
    } else {
      setRowStatuses(
        (current) => ({
          ...current,
          [key]: 'saved',
        })
      );
    }
  }

  /**
   * Save dirty goals sequentially.
   *
   * Every goal save autosaves ClinicalNoteMain and changes the parent
   * entityVersion, so parallel child saves must not reuse the same
   * expectedVersion.
   */
  const flushSaveQueue =
    useCallback(
      async () => {
        if (
          disabled ||
          !initialized ||
          savingRef.current
        ) {
          return;
        }

        savingRef.current =
          true;

        try {
          while (
            mountedRef.current &&
            dirtyKeysRef.current
              .size > 0
          ) {
            const key =
              Array.from(
                dirtyKeysRef.current
              ).find(
                (candidate) =>
                  !failedKeysRef
                    .current
                    .has(candidate)
              );

            if (!key) {
              break;
            }

            /*
             * Remove while the request is in flight.
             *
             * If another edit happens during the request,
             * updateRowField() adds it again automatically.
             */
            dirtyKeysRef.current.delete(
              key
            );

            const row =
              rowsRef.current.find(
                (candidate) =>
                  getGoalKey(
                    candidate
                  ) === key
              );

            if (!row) {
              continue;
            }

            const errors =
              validateGoalRow(
                row,
                statusOptionsRef.current
              );

            if (
              Object.keys(
                errors
              ).length > 0
            ) {
              setRowStatuses(
                (current) => ({
                  ...current,
                  [key]:
                    'invalid',
                })
              );

              continue;
            }

            const revisionBeingSaved =
              rowRevisionRef.current[
                key
              ] ?? 0;

            setRowStatuses(
              (current) => ({
                ...current,
                [key]: 'saving',
              })
            );

            setRowSaveErrors(
              (current) => ({
                ...current,
                [key]: '',
              })
            );

            try {
              // Another clinical section may have autosaved since the goals
              // loaded. Always resolve the latest version just before this
              // write, while retaining the server's optimistic-lock check.
              await refreshEntityVersion();

              const response =
                await clinicalNoteService
                  .saveGoal(
                    clinicalNoteId,
                    {
                      expectedVersion:
                        versionRef.current,

                      goalTemplateId:
                        row.goalTemplateId,

                      shortTermTargetValue:
                        row.shortTermEnabled
                          ? row
                              .draftShortTermTargetValue
                          : null,

                      shortTermCurrentValue:
                        row.shortTermEnabled
                          ? row
                              .draftShortTermCurrentValue
                          : null,

                      shortTermStatus:
                        row.shortTermEnabled
                          ? row
                              .draftShortTermStatus ||
                            null
                          : null,

                      longTermTargetValue:
                        row.longTermEnabled
                          ? row
                              .draftLongTermTargetValue
                          : null,

                      longTermCurrentValue:
                        row.longTermEnabled
                          ? row
                              .draftLongTermCurrentValue
                          : null,

                      longTermStatus:
                        row.longTermEnabled
                          ? row
                              .draftLongTermStatus ||
                            null
                          : null,

                      patientSpecificGoalText:
                        row
                          .allowPatientSpecificEdit
                          ? row
                              .draftPatientSpecificGoalText
                          : null,
                    }
                  );

              if (
                !mountedRef.current
              ) {
                return;
              }

              /*
               * Goal save updates the parent ClinicalNoteMain.
               * Refresh the version before any other child save.
               */
              const noteResponse =
                await refreshEntityVersion();

              if (
                !mountedRef.current
              ) {
                return;
              }

              applySuccessfulSave(
                key,
                response,
                revisionBeingSaved
              );
              await refreshGoalValidation();

              if (
                typeof onSaved ===
                'function'
              ) {
                onSaved(
                  response,
                  noteResponse
                );
              }
            } catch (error) {
              if (
                !mountedRef.current
              ) {
                return;
              }

              /*
               * Prevent continuous failed retries.
               *
               * The clinician may retry explicitly or make another
               * edit, which re-enables autosaving.
               */
              failedKeysRef.current.add(
                key
              );

              dirtyKeysRef.current.delete(
                key
              );

              setRowStatuses(
                (current) => ({
                  ...current,
                  [key]: 'error',
                })
              );

              setRowSaveErrors(
                (current) => ({
                  ...current,

                  [key]:
                    error?.message ||
                    'Clinical goal could not be autosaved.',
                })
              );
            }
          }
        } finally {
          savingRef.current =
            false;

          const hasRetryableDirtyRows =
            Array.from(
              dirtyKeysRef.current
            ).some(
              (key) =>
                !failedKeysRef
                  .current
                  .has(key)
            );

          if (
            mountedRef.current &&
            hasRetryableDirtyRows
          ) {
            window.setTimeout(
              () => {
                flushSaveRef
                  .current?.();
              },
              150
            );
          }
        }
      },
      [
        clinicalNoteId,
        disabled,
        initialized,
        onSaved,
        refreshEntityVersion,
        refreshGoalValidation,
      ]
    );

  /**
   * Share an awaitable save promise between debounced autosave and
   * visit navigation. A second caller must join the current save.
   */
  useEffect(() => {
    flushSaveRef.current = () => {
      if (inFlightSaveRef.current) return inFlightSaveRef.current;
      const running = flushSaveQueue();
      const tracked = Promise.resolve(running).finally(() => {
        if (inFlightSaveRef.current === tracked) {
          inFlightSaveRef.current = null;
        }
      });
      inFlightSaveRef.current = tracked;
      return tracked;
    };
  }, [flushSaveQueue]);

  /**
   * Register this section's save barrier with the visit navigator.
   * Unchanged/incomplete Draft fields are not treated as unsaved edits.
   * Automatically queued default targets for editable Drafts ARE edits
   * and must finish saving before the user leaves the visit.
   */
  useEffect(() => {
    if (!navigationCoordinator || !clinicalNoteId) return undefined;
    return navigationCoordinator.register(clinicalNoteId, 'goal', {
      flush: async () => {
        if (inFlightSaveRef.current) await inFlightSaveRef.current;
        if (dirtyKeysRef.current.size > 0) {
          await flushSaveRef.current?.();
        }
        if (inFlightSaveRef.current) await inFlightSaveRef.current;
      },
      getState: () => {
        const unsaved = [...unsavedKeysRef.current];
        const invalid = unsaved.some((key) => {
          const row = rowsRef.current.find(item => getGoalKey(item) === key);
          return row && Object.keys(validateGoalRow(row, statusOptionsRef.current)).length > 0;
        });
        const failed = unsaved.some(key => failedKeysRef.current.has(key));
        return {
          dirty: unsaved.length > 0 || !initializedRef.current,
          saving: Boolean(inFlightSaveRef.current),
          invalid,
          error: loadErrorRef.current ||
            (!initializedRef.current ? 'Goals are still loading.' : '') ||
            (failed ? 'A clinical goal failed to save. Correct it or use Retry Save before switching visits.' : ''),
        };
      },
    });
  }, [navigationCoordinator, clinicalNoteId]);

  /**
   * Debounced autosave trigger.
   */
  useEffect(() => {
    if (
      !initialized ||
      disabled ||
      dirtyKeysRef.current
        .size === 0
    ) {
      return undefined;
    }

    const timer =
      window.setTimeout(
        () => {
          flushSaveRef
            .current?.();
        },
        AUTOSAVE_DELAY_MS
      );

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    editTick,
    initialized,
    disabled,
  ]);

  /**
   * Retry one failed goal save.
   */
  function retryRow(key) {
    if (disabled) {
      return;
    }

    failedKeysRef.current.delete(
      key
    );

    dirtyKeysRef.current.add(
      key
    );

    setRowSaveErrors(
      (current) => ({
        ...current,
        [key]: '',
      })
    );

    setRowStatuses(
      (current) => ({
        ...current,
        [key]: 'unsaved',
      })
    );

    setEditTick(
      (current) =>
        current + 1
    );
  }

  /**
   * Current validation state for each goal.
   */
  const validationByKey =
    useMemo(() => {
      const result = {};

      rows.forEach(
        (row) => {
          result[
            getGoalKey(
              row
            )
          ] =
            validateGoalRow(
              row,
              statusOptions
            );
        }
      );

      return result;
    }, [
      rows,
      statusOptions,
    ]);

  /**
   * Overall section autosave state.
   */
  const overallStatus =
    useMemo(() => {
      const values =
        Object.values(
          rowStatuses
        );

      if (
        values.includes(
          'saving'
        )
      ) {
        return 'Saving…';
      }

      if (
        values.includes(
          'error'
        )
      ) {
        return 'Autosave error';
      }

      if (
        values.includes(
          'invalid'
        ) || values.includes('incomplete')
      ) {
        return 'Needs correction';
      }

      if (
        values.includes(
          'unsaved'
        )
      ) {
        return 'Unsaved changes';
      }

      return disabled
        ? 'Read only'
        : 'Saved';
    }, [
      rowStatuses,
      disabled,
    ]);

  /**
   * Count unresolved goal warnings.
   */
  const unresolvedWarningCount =
    useMemo(
      () =>
        rows.filter(
          (row) =>
            row.repetitionWarning &&
            !row.warningAcknowledged
        ).length,
      [rows]
    );

  if (loading) {
    return (
      <section
        style={{
          border:
            '1px solid #ddd',
          borderRadius: 10,
          padding: 16,
          background: '#fff',
        }}
      >
        Loading clinical goals…
      </section>
    );
  }

  if (loadError) {
    return (
      <section
        style={{
          border:
            '1px solid #c62828',
          borderRadius: 10,
          padding: 16,
          background:
            '#fff5f5',
        }}
      >
        <strong>
          Goals section could not be
          loaded
        </strong>

        <p
          style={{
            marginBottom: 0,
          }}
        >
          {loadError}
        </p>
      </section>
    );
  }

  return (
    <section
      style={{
        border:
          '1px solid #d7d7d7',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* =====================================================
          SECTION HEADER
          ===================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding:
            '13px 16px',
          background:
            '#f7f8fa',
          borderBottom:
            '1px solid #ddd',
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 17,
            }}
          >
            Clinical Goals
          </h3>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              color: '#666',
            }}
          >
            Short-Term and Long-Term
            goals are tracked against
            prior treatment visits.
          </div>
        </div>

        <div
          style={{
            textAlign: 'right',
          }}
        >
          <div
            aria-live="polite"
            style={{
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {overallStatus}
          </div>

          {unresolvedWarningCount >
            0 && (
            <div
              style={{
                marginTop: 3,
                fontSize: 12,
                color: '#9a6700',
                fontWeight: 700,
              }}
            >
              {unresolvedWarningCount}{' '}
              unresolved goal{' '}
              {unresolvedWarningCount ===
              1
                ? 'warning'
                : 'warnings'}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          padding: 16,
        }}
      >
        <details className="clinical-goal-compact-details clinical-goal-compact-help">
          <summary>Goal instructions</summary>
          <p>Goal targets use the configured patient-eligible templates. Current/progress and status may be optional depending on note type. Backend validation remains authoritative before review/signing.</p>
        </details>

        {/* ===================================================
            EMPTY STATE
            =================================================== */}
        {rows.length === 0 && (
          <div
            style={{
              border:
                '1px dashed #bbb',
              borderRadius: 8,
              padding: 16,
              color: '#666',
              background:
                '#fafafa',
            }}
          >
            No clinical goals are
            configured and eligible for
            this note.
          </div>
        )}

        {/* ===================================================
            GOAL ROWS
            =================================================== */}
        {rows.length > 0 && (
          <div
            style={{
              display: 'grid',
              gap: 14,
            }}
          >
            {rows.map(
              (row) => {
                const key =
                  getGoalKey(
                    row
                  );

                const goalIssues = backendGoalIssues.filter((issue) =>
                  issue.fieldCode === row.goalCode ||
                  (issue.recordId != null && issue.recordId === row.clinicalNoteGoalId)
                );

                return (
                  <GoalRow
                    key={key}
                    row={row}
                    statusOptions={
                      statusOptions
                    }
                    initialEvaluation={
                      initialEvaluation
                    }
                    disabled={
                      disabled
                    }
                    saveStatus={
                      rowStatuses[
                        key
                      ] ||
                      'saved'
                    }
                    saveError={
                      rowSaveErrors[
                        key
                      ] ||
                      ''
                    }
                    validationErrors={
                      validationByKey[
                        key
                      ] ||
                      {}
                    }
                    backendIssues={goalIssues}
                    onFieldChange={(
                      field,
                      value
                    ) =>
                      updateRowField(
                        key,
                        field,
                        value
                      )
                    }
                    onRetry={() =>
                      retryRow(
                        key
                      )
                    }
                  />
                );
              }
            )}
          </div>
        )}

        {disabled && (
          <div
            style={{
              marginTop: 14,
              fontSize: 13,
              color: '#666',
            }}
          >
            Goal documentation is read
            only because this clinical
            note is no longer in the
            normal Draft editing state.
          </div>
        )}
      </div>
    </section>
  );
}